import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useSessionContext } from "./SessionContext";
import { useOrganizationContext } from "./OrganizationContext";
import { GetContract } from "../services/fhir/contract/GetContract";
import { PostContract } from "../services/fhir/contract/PostContract";
import { PatchContract } from "../services/fhir/contract/PatchContract";
import { toast } from "react-toastify";

/**
 * Interface para o contexto do FluxSigner
 * Gerencia o contrato de assinatura digital
 */
interface FluxSignerContextValue {
  contractFluxSigner: any;
  setContractFluxSigner: (contract: any) => void;
  isLoading: boolean;
  activateFluxSigner: () => Promise<boolean>;
  deactivateFluxSigner: () => Promise<boolean>;
  refetchContract: () => Promise<void>;
}

export const FluxSignerContext = createContext<FluxSignerContextValue>(null!);

/**
 * Provider do contexto FluxSigner
 * Gerencia o estado do contrato de assinatura digital
 */
export function FluxSignerContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useSessionContext();
  const { organization } = useOrganizationContext();

  const [contractFluxSigner, setContractFluxSigner] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Busca o contrato FluxSigner existente
   */
  const fetchFluxSignerContract = useCallback(async () => {
    if (!organization?.id || !user?.username) return;

    setIsLoading(true);
    try {
      const response = await GetContract({
        organization: organization.id,
        identifier: "fluxsigner",
        status: "executed",
      });

      if (response?.entry && response.entry.length > 0) {
        const contract = response.entry[0].resource;
        setContractFluxSigner(contract);
      } else {
        setContractFluxSigner(null);
      }
    } catch (error) {
      console.error("❌ Erro ao buscar contrato FluxSigner:", error);
      setContractFluxSigner(null);
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, user?.username]);

  /**
   * Carrega o contrato ao montar o componente
   */
  useEffect(() => {
    fetchFluxSignerContract();
  }, [fetchFluxSignerContract]);

  /**
   * Ativa o FluxSigner criando um contrato
   */
  const activateFluxSigner = async (): Promise<boolean> => {
    if (!organization?.id || !user?.username) {
      toast.error("Dados da organização ou usuário não disponíveis");
      return false;
    }

    setIsLoading(true);
    try {
      const now = new Date().toISOString();
      
      // Cria o contrato FluxSigner no formato correto
      const contractData = {
        resourceType: "Contract",
        extension: [
          {
            url: "https://fluxmed.com.br/fhir/extension/fluxsigner/cpf",
            valueString: user.username,
          },
          {
            url: "https://fluxmed.com.br/fhir/extension/fluxsigner/nome",
            valueString: user.name || "",
          },
          {
            url: "https://fluxmed.com.br/fhir/extension/fluxsigner/email",
            valueString: user.email || "",
          },
        ],
        identifier: [
          {
            system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
            value: "fluxsigner",
          },
        ],
        status: "executed",
        authority: [
          {
            reference: `Organization/${organization.id}`,
          },
        ],
        type: {
          coding: [
            {
              system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
              code: "fluxsigner",
            },
          ],
        },
        term: [
          {
            identifier: {
              system: "https://fluxmed.com.br/hapi/fhir/R5/contract-terms",
              value: "fluxsigner",
            },
            issued: now,
            applies: {
              start: now,
            },
            text: "Contrato de extensão para utilização da assinatura digital FluxSigner",
            offer: {
              type: {
                coding: [
                  {
                    system: "https://fluxmed.com.br/hapi/fhir/R5/contract-offer-types",
                    code: "subscription",
                  },
                ],
              },
            },
            asset: [
              {
                valuedItem: [
                  {
                    entityCodeableConcept: {
                      coding: [
                        {
                          system: "https://fluxmed.com.br/hapi/fhir/R5/subscription-types",
                          code: "subscription-fee",
                        },
                      ],
                    },
                    quantity: {
                      value: 1,
                      unit: "subscription",
                    },
                    unitPrice: {
                      value: 0,
                      currency: "BRL",
                    },
                  },
                ],
              },
            ],
          },
        ],
      };

      const response = await PostContract({ data: contractData });

      if (response) {
        await fetchFluxSignerContract();
        toast.success("FluxSigner ativado com sucesso!");
        return true;
      }

      toast.error("Erro ao ativar FluxSigner");
      return false;
    } catch (error) {
      console.error("❌ Erro ao ativar FluxSigner:", error);
      toast.error("Erro ao ativar FluxSigner. Tente novamente.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Desativa o FluxSigner (atualiza o status do contrato para cancelled usando PATCH)
   */
  const deactivateFluxSigner = async (): Promise<boolean> => {
    if (!contractFluxSigner?.id) {
      toast.error("Nenhum contrato FluxSigner ativo encontrado");
      return false;
    }

    setIsLoading(true);
    try {
      // Usa PatchContract para atualizar apenas o status para "cancelled"
      await PatchContract({
        contractId: contractFluxSigner.id,
        operation: "replace",
        path: "/status",
        content: "cancelled",
      });

      setContractFluxSigner(null);
      toast.success("FluxSigner cancelado com sucesso!");
      return true;
    } catch (error) {
      console.error("❌ Erro ao desativar FluxSigner:", error);
      toast.error("Erro ao desativar FluxSigner. Tente novamente.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Recarrega o contrato
   */
  const refetchContract = async () => {
    await fetchFluxSignerContract();
  };

  const contextValue = {
    contractFluxSigner,
    setContractFluxSigner,
    isLoading,
    activateFluxSigner,
    deactivateFluxSigner,
    refetchContract,
  };

  return (
    <FluxSignerContext.Provider value={contextValue}>
      {children}
    </FluxSignerContext.Provider>
  );
}

/**
 * Hook para usar o contexto FluxSigner
 */
export function useFluxSignerContext() {
  const context = useContext(FluxSignerContext);
  if (!context) {
    throw new Error("useFluxSignerContext deve ser usado dentro de FluxSignerContextProvider");
  }
  return context;
}

